import React from "react"
import { Text, View, StyleSheet } from "@react-pdf/renderer"
import {
	createColumnHelper,
	flexRender,
	getCoreRowModel,
	useReactTable,
} from "@tanstack/react-table"
import { sum } from "@/utils/array"

const styles = StyleSheet.create({
	table: {
		border: "1px solid #dadada",
		borderRadius: 8,
	},
	tableHeader: {
		borderTopLeftRadius: 7,
		borderTopRightRadius: 7,
		backgroundColor: "#E2E8F0",
		borderBottomColor: "#dadada",
		fontWeight: "semibold",
	},
	tableTitle: {
		fontSize: 16,
		fontWeight: "bold",
	},
	tableCell: {
		fontSize: 8,
		padding: 12,
		paddingVertical: 6,
		flexDirection: "row",
		alignItems: "flex-start",
		justifyContent: "flex-end",
	},
	tableCell2: {
		fontSize: 9,
		padding: 4,
		justifyContent: "center",
	},
	footerCell: {
		paddingVertical: 4,
		paddingHorizontal: 12,
		fontSize: 8,
		justifyContent: "center",
		fontWeight: "bold",
	},
	tableRow: {
		flexDirection: "row",
		borderBottomWidth: 1,
		borderBottomColor: "#dadada",
	},
	tableFooter: {
		borderBottomColor: "#dadada",
		backgroundColor: "#F1F5F9",
		flexDirection: "row",
		borderBottomWidth: 1,
	},
	tableFooterLast: {
		backgroundColor: "#F1F5F9",
		flexDirection: "row",
		borderBottomLeftRadius: 7,
		borderBottomRightRadius: 7,
	},
	tableCellRight: {
		alignItems: "flex-end",
	},
})

export interface ReportDriveRow {
	when: string
	why: string
	start: string
	end: string
	notes: string
	rate: string
	distance: string
	value: string
	parking: string
	tolls: string
	total: string
}

interface ReportDrivePDFProps {
	rows: ReportDriveRow[]
	pageTotal: {
		distance: string
		value: string
		parking: string
		tolls: string
		total: string
	}
	reportTotal: {
		distance: string
		value: string
		parking: string
		tolls: string
		total: string
	}
}

const columnHelper = createColumnHelper<ReportDriveRow>()

const columns = [
	columnHelper.accessor("when", {
		header: () => {
			return <Text>Date of Trip</Text>
		},
		cell: (info) => {
			return info.getValue()
		},
		size: 100,
	}),
	columnHelper.accessor("why", {
		header: () => {
			return <Text>Purpose</Text>
		},
		cell: (info) => info.getValue(),
		size: 80,
	}),
	columnHelper.accessor("start", {
		header: () => {
			return <Text>Start Address</Text>
		},
		cell: (info) => info.getValue(),
		size: 250,
	}),
	columnHelper.accessor("end", {
		header: () => {
			return <Text>Finish Address</Text>
		},
		cell: (info) => info.getValue(),
		size: 250,
	}),
	columnHelper.accessor("rate", {
		header: () => {
			return <Text style={{ textAlign: "right" }}>Rate</Text>
		},
		cell: (info) => info.getValue(),
		size: 90,
	}),
	columnHelper.accessor("distance", {
		header: () => {
			return <Text style={{ textAlign: "right" }}>Distance</Text>
		},
		cell: (info) => info.getValue(),
		size: 90,
	}),
	columnHelper.accessor("value", {
		header: () => {
			return <Text style={{ textAlign: "right" }}>Amount</Text>
		},
		cell: (info) => info.getValue(),
		size: 90,
	}),
]
const alignments = ["left", "left", "left", "left", "right", "right", "right"] as const
const alignmentsFlex = [
	"flex-start",
	"flex-start",
	"flex-start",
	"flex-start",
	"flex-end",
	"flex-end",
	"flex-end",
] as const

export const ReportDrivePDF: React.FC<ReportDrivePDFProps> = ({
	rows,
	reportTotal,
	pageTotal,
}) => {
	const table = useReactTable({
		data: rows,
		columns,
		getCoreRowModel: getCoreRowModel(),
	})

	const headers = table.getHeaderGroups()[0].headers
	return (
		<View style={styles.table}>
			<View style={styles.tableHeader}>
				{table.getHeaderGroups().map((headerGroup) => {
					return (
						<View style={[styles.tableRow]} key={headerGroup.id}>
							{headerGroup.headers.map((header, index, headers) => (
								<View
									style={[
										styles.tableCell,
										{
											width: header.getSize(),
											justifyContent: alignmentsFlex[index],
										},
									]}
									key={header.id}
								>
									{header.isPlaceholder
										? null
										: flexRender(
												header.column.columnDef.header,
												header.getContext()
										  )}
								</View>
							))}
						</View>
					)
				})}
			</View>
			<View>
				{table.getRowModel().rows.map((row, index) => (
					<View
						style={[
							styles.tableRow,
							index % 2 === 1 ? { backgroundColor: "#F8FAFC" } : {},
						]}
						key={row.id}
					>
						{row.getVisibleCells().map((cell, index) => (
							<View
								style={[
									styles.tableCell,
									{
										width: cell.getContext().column.getSize(),
										justifyContent: alignmentsFlex[index],
									},
								]}
								key={cell.id}
							>
								<Text>
									{flexRender(
										cell.column.columnDef.cell,
										cell.getContext()
									)}
								</Text>
							</View>
						))}
					</View>
				))}
			</View>
			<View style={[styles.tableFooter]}>
				<View
					style={[
						styles.footerCell,
						{
							width: sum(headers.slice(0, 3), (x) => x.getSize()),
						},
					]}
				></View>
				<View style={[styles.footerCell, { width: headers[3].getSize() }]}>
					<Text style={{ textAlign: "right" }}>Page Totals</Text>
				</View>
				<View
					style={[
						styles.footerCell,
						{
							width: headers[4].getSize(),
						},
						{ textAlign: alignments[4] },
					]}
				></View>
				<View style={[styles.footerCell, { width: headers[5].getSize() }]}>
					<Text style={{ textAlign: alignments[5] }}>{pageTotal.distance}</Text>
				</View>
				<View style={[styles.footerCell, { width: headers[6].getSize() }]}>
					<Text
						style={{
							textAlign: alignments[6],
						}}
					>
						{pageTotal.value}
					</Text>
				</View>
			</View>
			<View style={[styles.tableFooterLast]}>
				<View
					style={[
						styles.footerCell,
						{
							width: sum(headers.slice(0, 3), (x) => x.getSize()),
						},
					]}
				></View>
				<View style={[styles.footerCell, { width: headers[3].getSize() }]}>
					<Text style={{ textAlign: "right" }}>Report Totals</Text>
				</View>
				<View
					style={[
						styles.footerCell,
						{
							width: headers[4].getSize(),
						},
						{ textAlign: alignments[4] },
					]}
				></View>
				<View style={[styles.footerCell, { width: headers[5].getSize() }]}>
					<Text style={{ textAlign: alignments[5] }}>
						{reportTotal.distance}
					</Text>
				</View>
				<View style={[styles.footerCell, { width: headers[6].getSize() }]}>
					<Text
						style={{
							textAlign: alignments[6],
						}}
					>
						{reportTotal.value}
					</Text>
				</View>
			</View>
		</View>
	)
}
